import {
    addFieldsToArray,
    buildCertificateField,
    buildField,
    getFieldValueIfDirty,
    removeFromArray,
    updateTabField,
    updateTabFieldArray
} from '../../common/utils';
import { SUBCATEGORIES } from '../../common/categories';
import {
    ADMIN_SECURITY_ROLES_FIELDS,
    IS_NEW_FIELD,
    SECURITY_MAIN_FIELDS,
    SF_SUPPORT_FIELDS,
    SSO_FIELDS
} from '../../common/fields';
import * as actions from './securityActions';
import { commonActionsReducer } from '../../common/actions';
import { PERMISSION_NAME_MAPPER, ROLES, FEATURE_FLAGS_NAMES } from '@silverfort-nodelibs/common/utils/consts';
import { isFeatureActive } from '@silverfort-nodelibs/common/utils/common';

import { validateAuthnMethodRequired } from '../../common/validators';

const { SECURITY_MAIN, ADMIN_SECURITY_ROLES, SSO_CONFIG, SF_SUPPORT } = SUBCATEGORIES;
const {
    ADMIN_CONSOLE_ACCOUNT,
    SESSION_TIMEOUT,
    CERTIFICATE_REQUEST,
    CERTIFICATE,
    HTTP_PROXY,
    HTTPS_PROXY,
    PROXY_ACCOUNT,
    DR_RECOVERY
} = SECURITY_MAIN_FIELDS;
const {
    SSO_ENABLED,
    SSO_PROTOCOL,
    SSO_CALLBACK_URL,
    SSO_LOGIN_URL,
    SSO_IDP_ISSUER,
    SSO_BUTTON_TEXT,
    SSO_ENABLED_AUTHN_CONTEXT,
    SSO_AUTHN_CONTEXT,
    SSO_CERTIFICATE
} = SSO_FIELDS;
const { SF_SUPPORT_ENABLED, SEND_DIAGNOSTICS, SEND_DIAGNOSTICS_ANON } = SF_SUPPORT_FIELDS;
const {
    ROLE_NAME,
    GUIDS,
    DASHBOARD,
    POLICIES,
    SERVICE_ACCOUNTS,
    APP_DISCOVERY,
    INSIGHTS,
    USERS,
    EXCLUDE_USERS,
    DELETE_TOKENS,
    TEST_USER_TOKENS,
    SEND_ENROLLMENT_EMAIL,
    SEND_ENROLLMENT_EMAIL_TO_UNPAIRED,
    MANAGE_PROTECTED_GROUPS,
    SET_EMAIL,
    MATCH_USERS,
    DISABLE_TOKENS,
    LOGS,
    PRIVILEGED_ACCOUNTS,
    INVENTORY,
    INVENTORY_AD,
    INVENTORY_CLOUD,
    SYSTEM_STATUS,
    TOGGLE_SYSTEM_POWER,
    PREFORM_ADSYNC,
    NOTIFICATIONS,
    SOFTWARE_UPDATES,
    SETTINGS,
    AUTHORIZE,
    TEST_MFA_TOKENS,
    RISK,
    ACCESS_ANALYSIS,
    CLOUD_NHI,
    CLOUD_INTEGRATIONS,
    CLOUD_ACCESS_ANALYSIS,
    CLOUD_AI_AGENT_SECURITY,
    CLOUD_AI_ACCESS_CONTROL,
    CLOUD_INSIGHTS,
    CLOUD_RISK
} = ADMIN_SECURITY_ROLES_FIELDS;

export const fieldsReducer = (state, action) => {
    let newState = state;
    switch (action.type) {
        case actions.UPDATE_SECURITY_MAIN:
            return updateTabField(state, SECURITY_MAIN.ID, action.payload.fieldId, action.payload.value);
        case actions.ENABLE_SF_SUPPORT:
            return updateTabField(state, SF_SUPPORT.ID, action.payload.fieldId, action.payload.value);
        case actions.SHARE_DIAG:
            return updateTabField(state, SF_SUPPORT.ID, action.payload.fieldId, action.payload.value);
        case actions.SHARE_DIAG_ANON:
            return updateTabField(state, SF_SUPPORT.ID, action.payload.fieldId, action.payload.value);
        case actions.UPDATE_SSO_CONFIG:
            newState = updateTabField(state, SSO_CONFIG.ID, action.payload.fieldId, action.payload.value);
            if (
                action.payload.fieldId === SSO_ENABLED_AUTHN_CONTEXT.ID ||
                action.payload.fieldId === SSO_AUTHN_CONTEXT.ID
            ) {
                validateAuthnMethodRequired(newState[SSO_CONFIG.ID].fields);
            }
            return newState;
        case actions.UPDATE_SECURITY_ROLE:
            return updateTabFieldArray(
                state,
                ADMIN_SECURITY_ROLES.ID,
                action.payload.fieldId,
                action.payload.index,
                action.payload.arrFieldId,
                action.payload.value
            );
        case actions.ADD_SECURITY_ROLE:
            const defaultPermissions = {};
            for (const permissionKey in PERMISSION_NAME_MAPPER) {
                defaultPermissions[permissionKey] = ROLES.EDITOR.value;
            }
            const newRole = mapSecurityRolesFields([
                {
                    name: '',
                    guids: [],
                    permissions: defaultPermissions,
                    isNew: true
                }
            ]);
            return addFieldsToArray(state, ADMIN_SECURITY_ROLES.ID, ADMIN_SECURITY_ROLES.ID, newRole[0]);
        case actions.REMOVE_SECURITY_ROLE:
            return removeFromArray(state, ADMIN_SECURITY_ROLES.ID, ADMIN_SECURITY_ROLES.ID, action.payload);
        case actions.UPDATE_PROXY_DATA:
            for (const proxyDataFieldId in action.payload.proxyData) {
                newState = updateTabField(
                    newState,
                    SECURITY_MAIN.ID,
                    proxyDataFieldId,
                    action.payload.proxyData[proxyDataFieldId]
                );
                newState[SECURITY_MAIN.ID].fields[proxyDataFieldId].isDirty = false;
            }
            return newState;

        default:
            return commonActionsReducer(state, action, initFieldsState);
    }
};
const validateProxyData = (proxyServerRegexStr, proxyVal, fieldName) => {
    const proxyServerRegex = new RegExp(proxyServerRegexStr);
    if (proxyVal && !proxyServerRegex.test(proxyVal.toLowerCase())) {
        return `${fieldName} must contain a valid URL`;
    }
};
export const initFieldsState = ({ settings }) => {
    return {
        [SECURITY_MAIN.ID]: {
            id: SECURITY_MAIN.ID,
            name: SECURITY_MAIN.NAME,
            fields: {
                [ADMIN_CONSOLE_ACCOUNT.ID]: buildField(ADMIN_CONSOLE_ACCOUNT, {
                    user: settings.ConsoleUsername,
                    pass: '',
                    oldPass: ''
                }),
                [SESSION_TIMEOUT.ID]: buildField(SESSION_TIMEOUT, settings.SessionTimeout, {
                    defined: true,
                    positiveNumber: true
                }),
                [HTTP_PROXY.ID]: buildField(HTTP_PROXY, settings.HTTPProxy, {
                    customValidator: (proxyVal, fieldName) =>
                        validateProxyData(settings.proxyServerRegex, proxyVal, fieldName)
                }),
                [HTTPS_PROXY.ID]: buildField(HTTPS_PROXY, settings.HTTPSProxy, {
                    customValidator: (proxyVal, fieldName) =>
                        validateProxyData(settings.proxyServerRegex, proxyVal, fieldName)
                }),
                [PROXY_ACCOUNT.ID]: buildField(PROXY_ACCOUNT, {
                    user: settings.ProxyUsername,
                    pass: ''
                }),
                [CERTIFICATE_REQUEST.ID]: buildField(CERTIFICATE_REQUEST, {
                    data: settings.TowerCertificateRequest,
                    fqdn: '',
                    san: ''
                }),
                [CERTIFICATE.ID]: buildCertificateField(
                    CERTIFICATE,
                    settings.TowerCertificateData || '',
                    {},
                    { refreshPageNeeded: true }
                ),
                [DR_RECOVERY.ID]: buildField(DR_RECOVERY, settings.drRecovery, { validateDrDuration: true })
            },
            validators: {}
        },
        [SSO_CONFIG.ID]: {
            id: SSO_CONFIG.ID,
            name: SSO_CONFIG.NAME,
            fields: {
                [SSO_ENABLED.ID]: buildField(SSO_ENABLED, settings.SSOEnabled),
                [SSO_PROTOCOL.ID]: buildField(SSO_PROTOCOL, settings.SSOProtocol),
                [SSO_CALLBACK_URL.ID]: buildField(SSO_CALLBACK_URL, settings.SSOCallbackUrl, {
                    notWhitespace: true,
                    validateSSOCallback: true
                }),
                [SSO_LOGIN_URL.ID]: buildField(SSO_LOGIN_URL, settings.SSOLoginUrl, {
                    notWhitespace: true
                }),
                [SSO_IDP_ISSUER.ID]: buildField(SSO_IDP_ISSUER, settings.SSOIdpIssuer, {
                    notWhitespace: true
                }),
                [SSO_BUTTON_TEXT.ID]: buildField(SSO_BUTTON_TEXT, settings.SSOButtonText, {
                    notWhitespace: true,
                    maxLength: 30
                }),
                [SSO_ENABLED_AUTHN_CONTEXT.ID]: buildField(
                    SSO_ENABLED_AUTHN_CONTEXT,
                    !settings.SSODisableRequestedAuthnContext
                ),
                [SSO_AUTHN_CONTEXT.ID]: {
                    ...buildField(SSO_AUTHN_CONTEXT, settings.SSOAuthnContext),
                    options: settings.SSOAuthnOptions
                },
                [SSO_CERTIFICATE.ID]: buildCertificateField(SSO_CERTIFICATE, settings.SSOCertificateData || '')
            },
            validators: {
                validateAuthnMethodRequired: true
            }
        },
        [ADMIN_SECURITY_ROLES.ID]: {
            id: ADMIN_SECURITY_ROLES.ID,
            name: ADMIN_SECURITY_ROLES.NAME,
            fields: {
                [ADMIN_SECURITY_ROLES.ID]: buildField(
                    ADMIN_SECURITY_ROLES,
                    mapSecurityRolesFields(settings.AdminSecurityRoles)
                )
            },
            validators: {}
        },
        [SF_SUPPORT.ID]: {
            id: SF_SUPPORT.ID,
            name: SF_SUPPORT.NAME,
            fields: {
                [SF_SUPPORT_ENABLED.ID]: buildField(SF_SUPPORT_ENABLED, settings.SaasSupportEnabled),
                [SEND_DIAGNOSTICS.ID]: buildField(SEND_DIAGNOSTICS, settings.DumperSendDailyAutomatically),
                [SEND_DIAGNOSTICS_ANON.ID]: buildField(SEND_DIAGNOSTICS_ANON, settings.DumperSendAutoAnonymized)
            }
        }
    };
};

export const mapToSettings = (saveAll, selectedTab, fields, currentSettings) => {
    const newSettings = {};
    if (saveAll || selectedTab === SECURITY_MAIN.ID) {
        const mainFields = fields[SECURITY_MAIN.ID].fields;
        newSettings.ConsoleUsername = getFieldValueIfDirty(
            mainFields[ADMIN_CONSOLE_ACCOUNT.ID],
            mainFields[ADMIN_CONSOLE_ACCOUNT.ID].value.user
        );
        newSettings.ConsolePassword = getFieldValueIfDirty(
            mainFields[ADMIN_CONSOLE_ACCOUNT.ID],
            mainFields[ADMIN_CONSOLE_ACCOUNT.ID].value.pass
        );
        newSettings.ConsoleOldPassword = getFieldValueIfDirty(
            mainFields[ADMIN_CONSOLE_ACCOUNT.ID],
            mainFields[ADMIN_CONSOLE_ACCOUNT.ID].value.oldPass
        );
        newSettings.SessionTimeout = getFieldValueIfDirty(mainFields[SESSION_TIMEOUT.ID]);
        newSettings.TowerCertificateRequest = getFieldValueIfDirty(
            mainFields[CERTIFICATE_REQUEST.ID],
            mainFields[CERTIFICATE_REQUEST.ID].value.data
        );
        newSettings.TowerCertificateData = getFieldValueIfDirty(mainFields[CERTIFICATE.ID]);
        newSettings.HTTPProxy = getFieldValueIfDirty(mainFields[HTTP_PROXY.ID]);
        newSettings.HTTPSProxy = getFieldValueIfDirty(mainFields[HTTPS_PROXY.ID]);
        newSettings.ProxyUsername = getFieldValueIfDirty(
            mainFields[PROXY_ACCOUNT.ID],
            mainFields[PROXY_ACCOUNT.ID].value.user
        );
        newSettings.ProxyPassword = getFieldValueIfDirty(
            mainFields[PROXY_ACCOUNT.ID],
            mainFields[PROXY_ACCOUNT.ID].value.pass
        );
        newSettings.drRecovery = getFieldValueIfDirty(mainFields[DR_RECOVERY.ID], mainFields[DR_RECOVERY.ID].value);
    }
    if (saveAll || selectedTab === SF_SUPPORT.ID) {
        const supportFields = fields[SF_SUPPORT.ID].fields;
        newSettings.SaasSupportEnabled = getFieldValueIfDirty(supportFields[SF_SUPPORT_ENABLED.ID]);
        newSettings.DumperSendDailyAutomatically = getFieldValueIfDirty(supportFields[SEND_DIAGNOSTICS.ID]);
        newSettings.DumperSendAutoAnonymized = getFieldValueIfDirty(supportFields[SEND_DIAGNOSTICS_ANON.ID]);
    }
    if (saveAll || selectedTab === SSO_CONFIG.ID) {
        const ssoFields = fields[SSO_CONFIG.ID].fields;
        newSettings.SSOEnabled = getFieldValueIfDirty(ssoFields[SSO_ENABLED.ID]);
        newSettings.SSOProtocol = getFieldValueIfDirty(ssoFields[SSO_PROTOCOL.ID]);
        newSettings.SSOCallbackUrl = getFieldValueIfDirty(ssoFields[SSO_CALLBACK_URL.ID]);
        newSettings.SSOLoginUrl = getFieldValueIfDirty(ssoFields[SSO_LOGIN_URL.ID]);
        newSettings.SSOIdpIssuer = getFieldValueIfDirty(ssoFields[SSO_IDP_ISSUER.ID]);
        newSettings.SSOButtonText = getFieldValueIfDirty(ssoFields[SSO_BUTTON_TEXT.ID]);
        newSettings.SSODisableRequestedAuthnContext = getFieldValueIfDirty(
            ssoFields[SSO_ENABLED_AUTHN_CONTEXT.ID],
            !ssoFields[SSO_ENABLED_AUTHN_CONTEXT.ID].value
        );
        newSettings.SSOAuthnContext = getFieldValueIfDirty(ssoFields[SSO_AUTHN_CONTEXT.ID]);
        newSettings.SSOCertificateData = getFieldValueIfDirty(ssoFields[SSO_CERTIFICATE.ID]);
    }
    if (saveAll || selectedTab === ADMIN_SECURITY_ROLES.ID) {
        const securityRolesFields = fields[ADMIN_SECURITY_ROLES.ID].fields;
        newSettings.AdminSecurityRoles = getFieldValueIfDirty(
            securityRolesFields[ADMIN_SECURITY_ROLES.ID],
            mapSettingsSecurityRoles(securityRolesFields[ADMIN_SECURITY_ROLES.ID].value, currentSettings)
        );
    }
    return newSettings;
};

const mapSettingsSecurityRoles = (roles, currentSettings) => {
    return roles.map((securityRole) => {
        const mappedRole = {};
        mappedRole.name = securityRole[ROLE_NAME.ID].value;
        mappedRole.guids = securityRole[GUIDS.ID].value;

        const rolePermissions = {
            [DASHBOARD.ID]: securityRole[DASHBOARD.ID].value,
            [POLICIES.ID]: securityRole[POLICIES.ID].value,
            [SERVICE_ACCOUNTS.ID]: securityRole[SERVICE_ACCOUNTS.ID].value,
            [APP_DISCOVERY.ID]: securityRole[APP_DISCOVERY.ID].value,
            [ACCESS_ANALYSIS.ID]: securityRole[ACCESS_ANALYSIS.ID].value,
            [INSIGHTS.ID]: securityRole[INSIGHTS.ID].value,
            [RISK.ID]: securityRole[RISK.ID].value,
            [USERS.ID]: securityRole[USERS.ID].value,
            [EXCLUDE_USERS.ID]: securityRole[EXCLUDE_USERS.ID].value,
            [DELETE_TOKENS.ID]: securityRole[DELETE_TOKENS.ID].value,
            [TEST_USER_TOKENS.ID]: securityRole[TEST_USER_TOKENS.ID].value,
            [SEND_ENROLLMENT_EMAIL.ID]: securityRole[SEND_ENROLLMENT_EMAIL.ID].value,
            [SEND_ENROLLMENT_EMAIL_TO_UNPAIRED.ID]: securityRole[SEND_ENROLLMENT_EMAIL_TO_UNPAIRED.ID].value,
            [MANAGE_PROTECTED_GROUPS.ID]: securityRole[MANAGE_PROTECTED_GROUPS.ID].value,
            [SET_EMAIL.ID]: securityRole[SET_EMAIL.ID].value,
            [MATCH_USERS.ID]: securityRole[MATCH_USERS.ID].value,
            [DISABLE_TOKENS.ID]: securityRole[DISABLE_TOKENS.ID].value,
            [LOGS.ID]: securityRole[LOGS.ID].value,
            [SYSTEM_STATUS.ID]: securityRole[SYSTEM_STATUS.ID].value,
            [TOGGLE_SYSTEM_POWER.ID]: securityRole[TOGGLE_SYSTEM_POWER.ID].value,
            [PREFORM_ADSYNC.ID]: securityRole[PREFORM_ADSYNC.ID].value,
            [NOTIFICATIONS.ID]: securityRole[NOTIFICATIONS.ID].value,
            [SOFTWARE_UPDATES.ID]: securityRole[SOFTWARE_UPDATES.ID].value,
            [PRIVILEGED_ACCOUNTS.ID]: securityRole[PRIVILEGED_ACCOUNTS.ID].value,
            [INVENTORY.ID]: securityRole[INVENTORY.ID].value,
            [INVENTORY_AD.ID]: securityRole[INVENTORY_AD.ID].value,
            [INVENTORY_CLOUD.ID]: securityRole[INVENTORY_CLOUD.ID].value,
            [SETTINGS.ID]: securityRole[SETTINGS.ID].value,
            [AUTHORIZE.ID]: securityRole[AUTHORIZE.ID].value,
            [TEST_MFA_TOKENS.ID]: securityRole[TEST_MFA_TOKENS.ID].value,
            [CLOUD_NHI.ID]: securityRole[CLOUD_NHI.ID].value,
            [CLOUD_INTEGRATIONS.ID]: securityRole[CLOUD_INTEGRATIONS.ID].value,
            [CLOUD_ACCESS_ANALYSIS.ID]: securityRole[CLOUD_ACCESS_ANALYSIS.ID].value,
            [CLOUD_AI_AGENT_SECURITY.ID]: securityRole[CLOUD_AI_AGENT_SECURITY.ID].value,
            [CLOUD_AI_ACCESS_CONTROL.ID]: securityRole[CLOUD_AI_ACCESS_CONTROL.ID].value,
            [CLOUD_INSIGHTS.ID]: securityRole[CLOUD_INSIGHTS.ID].value,
            [CLOUD_RISK.ID]: securityRole[CLOUD_RISK.ID].value
        };
        mappedRole.permissions = rolePermissions;
        return mappedRole;
    });
};

const mapSecurityRolesFields = (securityRoles) => {
    const securityRolesFields = [];
    for (const securityRole of securityRoles) {
        securityRolesFields.push({
            [ROLE_NAME.ID]: buildField(ROLE_NAME, securityRole.name, { required: true }),
            [GUIDS.ID]: buildField(GUIDS, securityRole.guids),

            [DASHBOARD.ID]: buildField(DASHBOARD, securityRole.permissions[DASHBOARD.ID]),
            [POLICIES.ID]: buildField(POLICIES, securityRole.permissions[POLICIES.ID]),
            [SERVICE_ACCOUNTS.ID]: buildField(SERVICE_ACCOUNTS, securityRole.permissions[SERVICE_ACCOUNTS.ID]),
            [APP_DISCOVERY.ID]: buildField(APP_DISCOVERY, securityRole.permissions[APP_DISCOVERY.ID]),
            [ACCESS_ANALYSIS.ID]: buildField(ACCESS_ANALYSIS, securityRole.permissions[ACCESS_ANALYSIS.ID]),
            [INSIGHTS.ID]: buildField(INSIGHTS, securityRole.permissions[INSIGHTS.ID]),
            [RISK.ID]: buildField(RISK, securityRole.permissions[RISK.ID]),
            [USERS.ID]: buildField(USERS, securityRole.permissions[USERS.ID]),
            [EXCLUDE_USERS.ID]: buildField(EXCLUDE_USERS, securityRole.permissions[EXCLUDE_USERS.ID]),
            [DELETE_TOKENS.ID]: buildField(DELETE_TOKENS, securityRole.permissions[DELETE_TOKENS.ID]),
            [TEST_USER_TOKENS.ID]: buildField(TEST_USER_TOKENS, securityRole.permissions[TEST_USER_TOKENS.ID]),
            [SEND_ENROLLMENT_EMAIL.ID]: buildField(
                SEND_ENROLLMENT_EMAIL,
                securityRole.permissions[SEND_ENROLLMENT_EMAIL.ID]
            ),
            [SEND_ENROLLMENT_EMAIL_TO_UNPAIRED.ID]: buildField(
                SEND_ENROLLMENT_EMAIL_TO_UNPAIRED,
                securityRole.permissions[SEND_ENROLLMENT_EMAIL_TO_UNPAIRED.ID]
            ),
            [MANAGE_PROTECTED_GROUPS.ID]: buildField(
                MANAGE_PROTECTED_GROUPS,
                securityRole.permissions[MANAGE_PROTECTED_GROUPS.ID]
            ),
            [SET_EMAIL.ID]: buildField(SET_EMAIL, securityRole.permissions[SET_EMAIL.ID]),
            [MATCH_USERS.ID]: buildField(MATCH_USERS, securityRole.permissions[MATCH_USERS.ID]),
            [DISABLE_TOKENS.ID]: buildField(DISABLE_TOKENS, securityRole.permissions[DISABLE_TOKENS.ID]),
            [LOGS.ID]: buildField(LOGS, securityRole.permissions[LOGS.ID]),
            [SYSTEM_STATUS.ID]: buildField(SYSTEM_STATUS, securityRole.permissions[SYSTEM_STATUS.ID]),
            [TOGGLE_SYSTEM_POWER.ID]: buildField(TOGGLE_SYSTEM_POWER, securityRole.permissions[TOGGLE_SYSTEM_POWER.ID]),
            [PREFORM_ADSYNC.ID]: buildField(PREFORM_ADSYNC, securityRole.permissions[PREFORM_ADSYNC.ID]),
            [NOTIFICATIONS.ID]: buildField(NOTIFICATIONS, securityRole.permissions[NOTIFICATIONS.ID]),
            [SOFTWARE_UPDATES.ID]: buildField(SOFTWARE_UPDATES, securityRole.permissions[SOFTWARE_UPDATES.ID]),
            [PRIVILEGED_ACCOUNTS.ID]: buildField(PRIVILEGED_ACCOUNTS, securityRole.permissions[PRIVILEGED_ACCOUNTS.ID]),
            [INVENTORY.ID]: buildField(INVENTORY, securityRole.permissions[INVENTORY.ID]),
            [INVENTORY_AD.ID]: buildField(INVENTORY_AD, securityRole.permissions[INVENTORY_AD.ID]),
            [INVENTORY_CLOUD.ID]: buildField(INVENTORY_CLOUD, securityRole.permissions[INVENTORY_CLOUD.ID]),
            [SETTINGS.ID]: buildField(SETTINGS, securityRole.permissions[SETTINGS.ID]),
            [AUTHORIZE.ID]: buildField(AUTHORIZE, securityRole.permissions[AUTHORIZE.ID]),
            [TEST_MFA_TOKENS.ID]: buildField(TEST_MFA_TOKENS, securityRole.permissions[TEST_MFA_TOKENS.ID]),
            [IS_NEW_FIELD.ID]: buildField(IS_NEW_FIELD, securityRole.isNew),
            [CLOUD_NHI.ID]: buildField(CLOUD_NHI, securityRole.permissions[CLOUD_NHI.ID]),
            [CLOUD_INTEGRATIONS.ID]: buildField(CLOUD_INTEGRATIONS, securityRole.permissions[CLOUD_INTEGRATIONS.ID]),
            [CLOUD_ACCESS_ANALYSIS.ID]: buildField(
                CLOUD_ACCESS_ANALYSIS,
                securityRole.permissions[CLOUD_ACCESS_ANALYSIS.ID]
            ),
            [CLOUD_AI_AGENT_SECURITY.ID]: buildField(
                CLOUD_AI_AGENT_SECURITY,
                securityRole.permissions[CLOUD_AI_AGENT_SECURITY.ID]
            ),
            [CLOUD_AI_ACCESS_CONTROL.ID]: buildField(
                CLOUD_AI_ACCESS_CONTROL,
                securityRole.permissions[CLOUD_AI_ACCESS_CONTROL.ID]
            ),
            [CLOUD_INSIGHTS.ID]: buildField(CLOUD_INSIGHTS, securityRole.permissions[CLOUD_INSIGHTS.ID]),
            [CLOUD_RISK.ID]: buildField(CLOUD_RISK, securityRole.permissions[CLOUD_RISK.ID])
        });
    }
    return securityRolesFields;
};
